import React, { useEffect, useMemo, useState } from "react";
import {
    Box,
    Grid,
    Card,
    CardContent,
    Typography,
    CircularProgress,
    Alert,
    Tabs,
    Tab,
    Container,
    Button,
} from "@mui/material";
import { Download as DownloadIcon, Help as HelpIcon, TableChart as ExcelIcon } from "@mui/icons-material";
import { observer } from "mobx-react-lite";
import { authStore, supplierStore, statsStore } from "../stores";
import StatsChart from "./StatsChart";
import PagesStats from "./PagesStats";
import MediaStats from "./MediaStats";
import DeviceStats from "./DeviceStats";
import FlightStats from "./FlightStats";
import StatsFilters from "./StatsFilters";
import HelpModal from "./HelpModal";
import { exportToCsv, exportToExcel, formatDateForExcel } from "../utils/excelExport";
import { resolveStatsSupplierId } from "../utils/statsSupplier";

interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

function TabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`stats-tabpanel-${index}`}
            aria-labelledby={`stats-tab-${index}`}
            {...other}
        >
            {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
        </div>
    );
}

const Dashboard: React.FC = observer(() => {
    const currentUser = authStore.user;
    const currentSupplier = supplierStore.currentSupplier;
    const [tabValue, setTabValue] = useState(0);
    const [helpModalOpen, setHelpModalOpen] = useState(false);
    const isAdmin = currentUser?.role === "admin";
    const isClient = currentUser?.role === "client";
    const isCinemaSupplier = currentUser?.role === "supplier" && ["okko", "wink", "premiere"].includes(currentUser.login || "");

    const tabIndexes = useMemo(() => {
        const pages = !isClient && !isCinemaSupplier ? 1 : null;
        const media = pages === null ? 1 : 2;
        const device = isAdmin ? 3 : null;
        const flight = isClient || isCinemaSupplier ? 2 : isAdmin ? 4 : 3;
        const other = isClient || isCinemaSupplier ? 3 : isAdmin ? 5 : 4;
        return {
            general: 0,
            pages,
            media,
            device,
            flight,
            other,
        };
    }, [isAdmin, isClient, isCinemaSupplier]);

    const refreshForTab = (tab: number, supplierId: number) => {
        const { startDate, endDate } = statsStore.dateRange;
        const { clientId } = statsStore.filters;

        if (tab === tabIndexes.general) {
            void statsStore.refreshAllData(supplierId);
            return;
        }
        if (tabIndexes.pages !== null && tab === tabIndexes.pages) {
            void statsStore.fetchPageStats(clientId, startDate, endDate, supplierId);
            return;
        }
        if (tab === tabIndexes.media) {
            void statsStore.fetchMediaStats(clientId, startDate, endDate, supplierId);
            return;
        }
        if (tabIndexes.device !== null && tab === tabIndexes.device) {
            void statsStore.fetchDeviceStats(clientId, startDate, endDate, supplierId);
            return;
        }
        if (tab === tabIndexes.flight) {
            void Promise.allSettled([
                statsStore.fetchMediaStats(clientId, startDate, endDate, supplierId, true),
                statsStore.fetchFlightStats(clientId, startDate, endDate, supplierId),
            ]);
            return;
        }
        if (tab === tabIndexes.other) {
            void Promise.allSettled([
                statsStore.fetchPageStats(clientId, startDate, endDate, supplierId),
                statsStore.fetchMediaStats(clientId, startDate, endDate, supplierId, true),
                statsStore.fetchFlightStats(clientId, startDate, endDate, supplierId),
            ]);
        }
    };

    useEffect(() => {
        if (currentUser) {
            // Для клиентов автоматически устанавливаем clientId
            if (currentUser.role === "client" && currentUser.clientId) {
                statsStore.setClientFilter(currentUser.clientId);
            }

            // Загружаем данные поставщика
            supplierStore.fetchSuppliers().then(() => {
                // Определяем поставщика динамически: сначала по user.supplierId, затем по code/login
                const enabledSuppliers = supplierStore.suppliers.filter((s) => s.isEnabled);
                const normalizedLogin = (currentUser.login || "").toLowerCase();
                const normalizedLoginAlt = normalizedLogin === "premiere" ? "premier" : normalizedLogin;
                let supplier = null;

                if (currentUser.supplierId) {
                    supplier = supplierStore.getSupplierById(currentUser.supplierId) || null;
                }

                if (!supplier && normalizedLogin && normalizedLogin !== "admin") {
                    supplier =
                        enabledSuppliers.find((s) => s.code?.toLowerCase() === normalizedLogin) ||
                        enabledSuppliers.find((s) => s.code?.toLowerCase() === normalizedLoginAlt) ||
                        enabledSuppliers.find((s) => s.name?.toLowerCase().includes(normalizedLoginAlt)) ||
                        null;
                }

                // Для клиентов используем первый доступный поставщик
                // Для поставщиков и админов используем определенного поставщика
                if (currentUser.role === "client") {
                    // Клиент может видеть статистику по всем поставщикам или выбрать конкретного
                    // По умолчанию показываем первого поставщика, если есть
                    if (enabledSuppliers && enabledSuppliers.length > 0) {
                        supplierStore.setCurrentSupplier(enabledSuppliers[0]);
                        const statsSupplierId = resolveStatsSupplierId(enabledSuppliers[0], currentUser);
                        if (statsSupplierId) refreshForTab(0, statsSupplierId);
                    }
                } else if (currentUser.role === "admin") {
                    // Для админа по умолчанию открываем OKKO (если есть), иначе первого доступного
                    const preferredSupplier =
                        enabledSuppliers.find((s) => {
                            const code = (s.code || "").toLowerCase();
                            const name = (s.name || "").toLowerCase();
                            return code.includes("okko") || name.includes("okko");
                        }) || enabledSuppliers[0];
                    if (preferredSupplier) {
                        supplierStore.setCurrentSupplier(preferredSupplier);
                        const statsSupplierId = resolveStatsSupplierId(preferredSupplier, currentUser);
                        if (statsSupplierId) refreshForTab(0, statsSupplierId);
                    }
                } else if (supplier) {
                    supplierStore.setCurrentSupplier(supplier);
                    const statsSupplierId = resolveStatsSupplierId(supplier, currentUser);
                    if (statsSupplierId) refreshForTab(0, statsSupplierId);
                }
            });
        }
    }, [currentUser]);

    const handleStartDateChange = (date: Date | null) => {
        if (date) {
            const newEndDate = statsStore.dateRange.endDate;
            statsStore.setDateRange(date, newEndDate);
            const statsSupplierId = resolveStatsSupplierId(currentSupplier, currentUser);
            if (statsSupplierId) refreshForTab(tabValue, statsSupplierId);
        }
    };

    const handleEndDateChange = (date: Date | null) => {
        if (date) {
            const newStartDate = statsStore.dateRange.startDate;
            statsStore.setDateRange(newStartDate, date);
            const statsSupplierId = resolveStatsSupplierId(currentSupplier, currentUser);
            if (statsSupplierId) refreshForTab(tabValue, statsSupplierId);
        }
    };

    const handleClientChange = (clientId: number | null) => {
        statsStore.setClientFilter(clientId);
        const statsSupplierId = resolveStatsSupplierId(currentSupplier, currentUser);
        if (statsSupplierId) refreshForTab(tabValue, statsSupplierId);
    };

    const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
        setTabValue(newValue);
        const statsSupplierId = resolveStatsSupplierId(currentSupplier, currentUser);
        if (statsSupplierId) refreshForTab(newValue, statsSupplierId);
    };

    const handleClearFilters = () => {
        statsStore.clearFilters();
        statsStore.setDateRange(new Date(Date.now() - 30 * 24 * 60 * 60 * 1000), new Date());
        const statsSupplierId = resolveStatsSupplierId(currentSupplier, currentUser);
        if (statsSupplierId) refreshForTab(tabValue, statsSupplierId);
    };

    const handleExportGeneralStats = () => {
        try {
            const csvHeaders = ["Сериал", "Просмотры"];

            // Используем данные из графика (chartData) - топ медиа по просмотрам
            const chartData = statsStore.chartData || [];
            
            const csvRows: any[] = chartData.map((item: any) => [
                item.fullName || item.name || "Неизвестно",
                item.views || 0
            ]);

            // Добавляем общую информацию
            csvRows.push(
                ["Период", `${statsStore.dateRange.startDate.toLocaleDateString("ru-RU")} - ${statsStore.dateRange.endDate.toLocaleDateString("ru-RU")}`],
                ["Поставщик", currentSupplier?.name || "Не выбран"]
            );

            exportToCsv({
                filename: `выгрузка_топа_по_просмотрам_${new Date().toISOString().split("T")[0]}`,
                headers: csvHeaders,
                data: csvRows,
                separator: ";",
            });
        } catch (error) {
            console.error("Ошибка экспорта общей статистики:", error);
        }
    };

    const handleExportGeneralStatsExcel = () => {
        try {
            const chartData = statsStore.chartData || [];
            
            const headers = ["Сериал", "Просмотры"];
            const data = chartData.map((item: any) => [
                item.fullName || item.name || "Неизвестно",
                item.views || 0
            ]);

            const additionalInfo = [
                ["Период", `${formatDateForExcel(statsStore.dateRange.startDate)} - ${formatDateForExcel(statsStore.dateRange.endDate)}`],
                ["Поставщик", currentSupplier?.name || "Не выбран"]
            ];

            exportToExcel({
                filename: `выгрузка_топа_по_просмотрам_${new Date().toISOString().split("T")[0]}`,
                sheetName: "Топ по просмотрам",
                headers,
                data,
                additionalInfo: additionalInfo.map(([label, value]) => ({ label, value }))
            });
        } catch (error) {
            console.error("Ошибка экспорта общей статистики в Excel:", error);
        }
    };

    const formatDateForAPI = (date: Date | null): string | undefined => {
        return date ? date.toISOString().split("T")[0] : undefined;
    };

    if (supplierStore.loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress />
            </Box>
        );
    }

    if (supplierStore.error) {
        return (
            <Alert severity="error" sx={{ m: 2 }}>
                {supplierStore.error}
            </Alert>
        );
    }

    const suppliers = supplierStore.suppliers.filter((s) => s.isEnabled);

    // Для клиентов и админов поставщик может быть не выбран, но это нормально
    // Для поставщиков поставщик должен быть выбран
    if (!isAdmin && !isClient && !currentSupplier) {
        return (
            <Alert severity="warning" sx={{ m: 2 }}>
                Поставщик не найден
            </Alert>
        );
    }

    const handleSupplierSwitch = (supplierId: number) => {
        const supplier = supplierStore.getSupplierById(supplierId);
        if (supplier) {
            supplierStore.setCurrentSupplier(supplier);
            const statsSupplierId = resolveStatsSupplierId(supplier, currentUser);
            if (statsSupplierId) refreshForTab(tabValue, statsSupplierId);
        }
    };

    return (
        <Container maxWidth="xl" sx={{ py: 3 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h4">
                    {isAdmin ? "Панель администратора" : isClient ? "Личный кабинет клиента" : `Личный кабинет: ${currentSupplier?.name || "Не выбран"}`}
                </Typography>
                {!isAdmin && (
                    <Button
                        variant="outlined"
                        startIcon={<HelpIcon />}
                        onClick={() => setHelpModalOpen(true)}
                        sx={{ ml: 2 }}
                    >
                        Как пользоваться
                    </Button>
                )}
            </Box>

            {/* Переключатель поставщиков для админа и клиентов */}
            {(isAdmin || isClient) && suppliers.length > 0 && (
                <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 3 }}>
                    <Tabs
                        value={Math.max(0, suppliers.findIndex((s) => s.id === currentSupplier?.id))}
                        onChange={(event, newValue) => {
                            const supplier = suppliers[newValue];
                            if (supplier) {
                                handleSupplierSwitch(supplier.id);
                            }
                        }}
                        variant="scrollable"
                        scrollButtons="auto"
                    >
                        {suppliers.map((s) => (
                            <Tab key={s.id} label={s.name} />
                        ))}
                    </Tabs>
                </Box>
            )}

            {/* Рекламный слайдер - скрыт для поставщиков */}
            {/* {!isAdmin && <AdSlider />} */}

            {/* Фильтры */}
            <StatsFilters
                startDate={statsStore.dateRange.startDate}
                endDate={statsStore.dateRange.endDate}
                clientId={statsStore.filters.clientId}
                onStartDateChange={handleStartDateChange}
                onEndDateChange={handleEndDateChange}
                onClientChange={handleClientChange}
                onClearFilters={handleClearFilters}
            />

            {/* Табы для разных типов статистики */}
            <Card>
                <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
                    <Tabs value={tabValue} onChange={handleTabChange} aria-label="статистика">
                        <Tab label="Общая статистика" />
                        {!isClient && !isCinemaSupplier && <Tab label="Статистика страниц" />}
                        <Tab label="Статистика медиа" />
                        {isAdmin && <Tab label="Статистика устройств" />}
                        <Tab label="Статистика рейсов" />
                        {isAdmin && <Tab label="Прочая статистика" />}
                    </Tabs>
                </Box>

                {/* Общая статистика */}
                <TabPanel value={tabValue} index={0}>
                    <Box sx={{ p: 3 }}>
                        <Box display="flex" justifyContent="flex-end" alignItems="center" mb={2}>
                            <Button
                                variant="contained"
                                startIcon={<DownloadIcon />}
                                onClick={handleExportGeneralStats}
                                sx={{ mr: 2 }}
                            >
                                Экспорт CSV
                            </Button>
                            <Button
                                variant="contained"
                                color="success"
                                startIcon={<ExcelIcon />}
                                onClick={handleExportGeneralStatsExcel}
                            >
                                Экспорт Excel
                            </Button>
                        </Box>

                        <Grid container spacing={3} sx={{ mt: 2 }}>
                            {!isCinemaSupplier && (
                                <Grid size={{ xs: 12, md: 4 }}>
                                    <Card>
                                        <CardContent>
                                            <Typography variant="h6" gutterBottom>
                                                Страницы
                                            </Typography>
                                            <Typography variant="h3" color="info.main">
                                                {(
                                                    (statsStore.pageStats as any)?.totalViewCount ||
                                                    statsStore.pageStats?.data?.reduce(
                                                        (sum, page) => sum + parseInt(page.viewCount || "0"),
                                                        0,
                                                    ) ||
                                                    0
                                                ).toLocaleString()}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                Просмотров страниц
                                            </Typography>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            )}

                            <Grid size={{ xs: 12, md: 4 }}>
                                <Card>
                                    <CardContent>
                                        <Typography variant="h6" gutterBottom>
                                            Медиа контент
                                        </Typography>
                                        <Typography variant="h3" color="secondary">
                                            {(() => {
                                                const mediaStats = statsStore.mediaStats as any;
                                                // После нормализации в statsStore структура: {data: [], totalViewCount: number}
                                                const totalViewCount = mediaStats?.totalViewCount;
                                                if (totalViewCount !== undefined && totalViewCount !== null) {
                                                    return totalViewCount.toLocaleString();
                                                }
                                                const mediaData = Array.isArray(mediaStats?.data) ? mediaStats.data : [];
                                                return mediaData.reduce(
                                                    (sum: number, media: any) => sum + parseInt(media.viewCount || "0"),
                                                    0,
                                                ).toLocaleString();
                                            })()}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            Просмотров медиа
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Grid>

                            <Grid size={{ xs: 12, md: 4 }}>
                                <Card>
                                    <CardContent>
                                        <Typography variant="h6" gutterBottom>
                                            Период
                                        </Typography>
                                        <Typography variant="body1">
                                            {statsStore.dateRange.startDate.toLocaleDateString()} -{" "}
                                            {statsStore.dateRange.endDate.toLocaleDateString()}
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                        </Grid>

                        {/* Информационный баннер - скрыт для поставщиков */}
                        {/* <Alert severity="info" sx={{ mt: 3, mb: 3 }}>
              <Typography variant="body2">
                <strong>Общая статистика:</strong> Показывает все просмотры,
                включая просмотры на рейсах и вне рейсов . Для детальной
                статистики по рейсам или устройствам используйте соответствующие
                вкладки.
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                <strong>Важно:</strong> Зрители могут пересекаться между
                страницами и медиа, поэтому суммы зрителей по разным вкладкам
                могут НЕ совпадать с общей.
              </Typography>
            </Alert> */}

                        <Grid container spacing={3}>
                            {/* График статистики - расширен на всю ширину */}
                            <Grid size={{ xs: 12 }}>
                                <Card>
                                    <CardContent>
                                        <Typography variant="h6" gutterBottom>
                                            Топ по просмотрам
                                        </Typography>
                                        <StatsChart data={statsStore.chartData} loading={statsStore.loading} />
                                    </CardContent>
                                </Card>
                            </Grid>

                            {/* Список топ медиа - скрыт для поставщиков */}
                            {/* <Grid size={{ xs: 12, md: 4 }}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Топ 5 медиа
                    </Typography>
                    <TopMediaList
                      media={statsStore.topMedia}
                      loading={statsStore.loading}
                    />
                  </CardContent>
                </Card>
              </Grid> */}
                        </Grid>
                    </Box>
                </TabPanel>

                {/* Статистика страниц */}
                {!isClient && !isCinemaSupplier && (
                    <TabPanel value={tabValue} index={tabIndexes.pages ?? 1}>
                        <PagesStats
                            startDate={formatDateForAPI(statsStore.dateRange.startDate)}
                            endDate={formatDateForAPI(statsStore.dateRange.endDate)}
                            clientId={statsStore.filters.clientId || undefined}
                        />
                    </TabPanel>
                )}

                {/* Статистика медиа */}
                <TabPanel value={tabValue} index={tabIndexes.media}>
                    <MediaStats
                        startDate={formatDateForAPI(statsStore.dateRange.startDate)}
                        endDate={formatDateForAPI(statsStore.dateRange.endDate)}
                        clientId={statsStore.filters.clientId || undefined}
                    />
                </TabPanel>

                {/* Статистика устройств */}
                {isAdmin && (
                    <TabPanel value={tabValue} index={tabIndexes.device ?? 3}>
                        <DeviceStats
                            startDate={formatDateForAPI(statsStore.dateRange.startDate)}
                            endDate={formatDateForAPI(statsStore.dateRange.endDate)}
                            clientId={statsStore.filters.clientId || undefined}
                        />
                    </TabPanel>
                )}

                {/* Статистика рейсов */}
                <TabPanel value={tabValue} index={tabIndexes.flight}>
                    <FlightStats
                        startDate={formatDateForAPI(statsStore.dateRange.startDate)}
                        endDate={formatDateForAPI(statsStore.dateRange.endDate)}
                        clientId={statsStore.filters.clientId || undefined}
                    />
                </TabPanel>

                {/* Прочая статистика */}
                <TabPanel value={tabValue} index={tabIndexes.other}>
                    <Box sx={{ p: 3 }}>
                        <Typography variant="h5" gutterBottom>
                            Прочая статистика
                        </Typography>
                        {/* Информационный баннер - скрыт для поставщиков */}
                        {/* <Alert severity="info" sx={{ mb: 3 }}>
              <Typography variant="body2">
                <strong>Что это:</strong> просмотры, у которых не был указан
                рейс (рейс не заполнен).
              </Typography>
            </Alert> */}

                        {(() => {
                            const totalPageViews = ((statsStore.pageStats as any)?.totalViewCount ||
                                statsStore.pageStats?.data?.reduce(
                                    (sum, page) => sum + parseInt(page.viewCount || "0"),
                                    0,
                                ) ||
                                0) as number;
                            const mediaStatsAny = statsStore.mediaStats as any;
                            // После нормализации в statsStore структура: {data: [], totalViewCount: number}
                            const mediaData = Array.isArray(mediaStatsAny?.data) ? mediaStatsAny.data : [];
                            const totalMediaViews = (mediaStatsAny?.totalViewCount ||
                                mediaData.reduce(
                                    (sum: number, media: any) => sum + parseInt(media.viewCount || "0"),
                                    0,
                                ) ||
                                0) as number;

                            const flightPageViews = (statsStore.flightStats?.data?.reduce(
                                (sum, f) => sum + parseInt((f as any).pageViews || "0"),
                                0,
                            ) || 0) as number;
                            const flightMediaViews = (statsStore.flightStats?.data?.reduce(
                                (sum, f) => sum + parseInt((f as any).mediaViews || "0"),
                                0,
                            ) || 0) as number;

                            const outsidePageViews = Math.max(0, totalPageViews - flightPageViews);
                            const outsideMediaViews = Math.max(0, totalMediaViews - flightMediaViews);

                            return (
                                <Grid container spacing={3}>
                                    <Grid size={{ xs: 12, md: 6 }}>
                                        <Card>
                                            <CardContent>
                                                <Typography variant="h6" gutterBottom>
                                                    Просмотры страниц (вне рейсов)
                                                </Typography>
                                                <Typography variant="h3" color="info.main">
                                                    {outsidePageViews.toLocaleString()}
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    Расчет: общие страницы − страницы на рейсах
                                                </Typography>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                    <Grid size={{ xs: 12, md: 6 }}>
                                        <Card>
                                            <CardContent>
                                                <Typography variant="h6" gutterBottom>
                                                    Просмотры медиа (вне рейсов)
                                                </Typography>
                                                <Typography variant="h3" color="secondary">
                                                    {outsideMediaViews.toLocaleString()}
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    Расчет: общие медиа − медиа на рейсах
                                                </Typography>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                </Grid>
                            );
                        })()}
                    </Box>
                </TabPanel>
            </Card>

            {/* Модальное окно помощи */}
            <HelpModal open={helpModalOpen} onClose={() => setHelpModalOpen(false)} />
        </Container>
    );
});

export default Dashboard;
